import endpoints from '@/api/endpoints';
import { http, HttpResponse } from 'msw';
import { Loan, LoanPayments } from '@/interfaces/loan';

const API_BASE_URL = endpoints.baseURL;

// ── Mock data ─────────────────────────────────────────────────────────────

export const mockLoans: Loan[] = [
  {
    id: 'loan-001',
    loanId: 'LOAN-000001',
    loanName: 'Home Loan',
    status: 'ACTIVE',
    currency: 'XAF',
    loanAmount: 50000000,
    monthlyAmount: 1000000,
    nextPaymentAmount: 1000000,
    nextPaymentDate: '2026-05-13',
    totalLoanAmountPaid: 5000000,
  },
  {
    id: 'loan-002',
    loanId: 'LOAN-000002',
    loanName: 'Car Loan',
    status: 'ACTIVE',
    currency: 'XAF',
    loanAmount: 15000000,
    monthlyAmount: 350000,
    nextPaymentAmount: 350000,
    nextPaymentDate: '2026-05-13',
    totalLoanAmountPaid: 2100000,
  },
  {
    id: 'loan-003',
    loanId: 'LOAN-000003',
    loanName: 'Personal Loan',
    status: 'CLOSED',
    currency: 'XAF',
    loanAmount: 5000000,
    monthlyAmount: 200000,
    nextPaymentAmount: 0,
    nextPaymentDate: '2026-04-01',
    totalLoanAmountPaid: 5000000,
  },
];

export const mockLoanPayments: Record<string, LoanPayments[]> = {
  'loan-001': [
    {
      paymentNumber: 1,
      paymentDate: '2026-01-13',
      loanAmountPaid: 800000,
      interestAmountPaid: 200000,
      remainingLoan: 49200000,
    },
    {
      paymentNumber: 2,
      paymentDate: '2026-02-13',
      loanAmountPaid: 805000,
      interestAmountPaid: 195000,
      remainingLoan: 48395000,
    },
    {
      paymentNumber: 3,
      paymentDate: '2026-03-13',
      loanAmountPaid: 810000,
      interestAmountPaid: 190000,
      remainingLoan: 47585000,
    },
    {
      paymentNumber: 4,
      paymentDate: '2026-04-13',
      loanAmountPaid: 815000,
      interestAmountPaid: 185000,
      remainingLoan: 46770000,
    },
    {
      paymentNumber: 5,
      paymentDate: '2026-05-13',
      loanAmountPaid: 820000,
      interestAmountPaid: 180000,
      remainingLoan: 45950000,
    },
  ],
  'loan-002': [
    {
      paymentNumber: 1,
      paymentDate: '2026-02-13',
      loanAmountPaid: 280000,
      interestAmountPaid: 70000,
      remainingLoan: 14720000,
    },
    {
      paymentNumber: 2,
      paymentDate: '2026-03-13',
      loanAmountPaid: 283000,
      interestAmountPaid: 67000,
      remainingLoan: 14437000,
    },
    {
      paymentNumber: 3,
      paymentDate: '2026-04-13',
      loanAmountPaid: 286000,
      interestAmountPaid: 64000,
      remainingLoan: 14151000,
    },
    {
      paymentNumber: 4,
      paymentDate: '2026-05-13',
      loanAmountPaid: 289000,
      interestAmountPaid: 61000,
      remainingLoan: 13862000,
    },
  ],
  'loan-003': [
    {
      paymentNumber: 1,
      paymentDate: '2025-04-01',
      loanAmountPaid: 200000,
      interestAmountPaid: 0,
      remainingLoan: 4800000,
    },
    {
      paymentNumber: 2,
      paymentDate: '2025-05-01',
      loanAmountPaid: 200000,
      interestAmountPaid: 0,
      remainingLoan: 4600000,
    },
  ],
};

// ── Handlers ──────────────────────────────────────────────────────────────

/**
 * Get list of loans with pagination
 */
export const getLoansHandler = http.get<object, any, any>(`${API_BASE_URL}${endpoints.getLoans}`, async ({ request }) => {
  const url = new URL(request.url);
  const page = Number(url.searchParams.get('page')) || 0;
  const size = Number(url.searchParams.get('size')) || 5;

  const start = page * size;
  const end = start + size;
  const paginatedLoans = mockLoans.slice(start, end);

  return HttpResponse.json({
    data: paginatedLoans,
    totalElements: mockLoans.length,
    totalPages: Math.ceil(mockLoans.length / size),
    currentPage: page,
    size,
    isFirst: page === 0,
    isLast: end >= mockLoans.length,
  });
});

/**
 * Get loan payment details/history
 */
export const getLoanDetailsHandler = http.get<{ ID: string }, any, any>(`${API_BASE_URL}${endpoints.getLoanDetails}`, ({ params }) => {
  const { ID } = params;
  const payments = mockLoanPayments[ID] || [];

  if (!payments.length) {
    return HttpResponse.json(
      {
        code: 'LOAN_NOT_FOUND',
        message: { en: 'Loan not found', fr: 'Prêt non trouvé' },
      },
      { status: 404 }
    );
  }

  return HttpResponse.json({ payments });
});

/**
 * Loan simulation - calculate amortization schedule
 */
export const simulateLoanHandler = http.post<object, any, any>(`${API_BASE_URL}/payment/loans/simulate`, async ({ request }) => {
  const body = await request.json();
  const { loanAmount, duration, interestRate = 0.05, paymentFrequency = 'Monthly' } = body;

  // Simple calculation
  const frequencyMap: Record<string, number> = {
    Weekly: 52,
    Fortnightly: 26,
    '15 days': 24,
    Monthly: 12,
    Quarterly: 4,
    'Half-yearly': 2,
    Annually: 1,
  };

  const periodsPerYear = frequencyMap[paymentFrequency] || 12;
  const totalPeriods = (duration / 12) * periodsPerYear;
  const periodRate = interestRate / periodsPerYear;

  const monthlyPayment =
    (loanAmount * (periodRate * Math.pow(1 + periodRate, totalPeriods))) / (Math.pow(1 + periodRate, totalPeriods) - 1);

  const totalAmount = monthlyPayment * totalPeriods;
  const totalInterest = totalAmount - loanAmount;

  return HttpResponse.json({
    monthlyPayment: Math.round(monthlyPayment * 100) / 100,
    totalInterest: Math.round(totalInterest * 100) / 100,
    totalAmount: Math.round(totalAmount * 100) / 100,
    loanAmount,
    duration,
    paymentFrequency,
    interestRate,
  });
});

export const loanHandlers = [getLoansHandler, getLoanDetailsHandler, simulateLoanHandler];
